import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { WarmHandoffProcedureDate } from '@bariendo/lib/patient-access-warm-handoff-note';
import { NOT_READY_TO_COMMIT } from '@bariendo/lib/patient-access-warm-handoff-script';
import { useCallback } from 'react';
import { ProcedureDateOption } from './procedureDateOptions';

type WithProcedureDate = PatientAccessCallAnswers & { procedureDate?: WarmHandoffProcedureDate };

/**
 * Selecting and declining a procedure date on a call card.
 *
 * The date and the decline chip are mutually exclusive answers to one question,
 * so choosing either clears the other; re-tapping the current choice clears it,
 * so a misclick is recoverable. The agreed date is recorded on `procedureDate`
 * and mirrored into `values[valueId]`; the decline lands in `values` alone.
 *
 * Shared by the warm hand-off and the PN consultation, which ask the question
 * under different widget ids with identical semantics.
 */
export function useProcedureDateAnswer<T extends WithProcedureDate>(
  updateAnswers: (mutate: (current: T) => T) => void,
  valueId: string
): {
  handleSelectDate: (option: ProcedureDateOption) => void;
  handleDecline: () => void;
} {
  const handleSelectDate = useCallback(
    (option: ProcedureDateOption) => {
      updateAnswers((current) => {
        if (current.procedureDate?.start === option.date) {
          const { procedureDate: _dropped, ...rest } = current;
          return { ...rest, values: { ...current.values, [valueId]: undefined } } as T;
        }

        const procedureDate: WarmHandoffProcedureDate = {
          start: option.date,
          physicianReference: option.physicianReference ?? '',
          ...(option.physicianName ? { physicianName: option.physicianName } : {}),
        };

        return { ...current, procedureDate, values: { ...current.values, [valueId]: option.date } };
      });
    },
    [updateAnswers, valueId]
  );

  const handleDecline = useCallback(() => {
    updateAnswers((current) => {
      if (current.values[valueId] === NOT_READY_TO_COMMIT) {
        return { ...current, values: { ...current.values, [valueId]: undefined } };
      }
      // Recording "would not commit" drops any date previously agreed, so the note
      // cannot claim both.
      const { procedureDate: _dropped, ...rest } = current;
      return { ...rest, values: { ...current.values, [valueId]: NOT_READY_TO_COMMIT } } as T;
    });
  }, [updateAnswers, valueId]);

  return { handleSelectDate, handleDecline };
}
